<template>
  <clinician-app-layout>
    <template #header>
      <inertia-link
        :href="route('admin.learning-courses.index')"
        class="lg:absolute relative p-3 md:left-5 flex items-center text-vice-teal-500 text-xs"
      >
        <ChevronLeftIcon class="w-4 h-4 mr-1" />
        <span class="flex-none">All Courses</span>
      </inertia-link>
      <div
        class="pb-5 max-w-6xl mx-auto border-gray-200 sm:flex sm:items-center sm:justify-between"
      >
        <h2 class="text-3xl text-vice-teal-900 font-semibold leading-tight">Create course</h2>

        <div class="mt-3 flex sm:mt-0 sm:ml-4">
          <inertia-link
            :href="route('admin.learning-courses.index')"
            class="
              py-2
              px-4
              text-md
              font-medium
              rounded-md
              border
              hover:bg-gray-50
              border-gray-300
              text-gray-700
            "
          >
            Cancel
          </inertia-link>
          <button
            data-cy="create-course"
            type="button"
            @click="form.processing ? 'javascript:info(0)' : submit()"
            :class="form.processing ? 'disabled:cursor-wait disabled:opacity-60' : ''"
            class="
              ml-3
              inline-flex
              items-center
              px-4
              py-2
              border border-transparent
              rounded-md
              shadow-sm
              text-md
              font-medium
              text-white
              bg-vice-teal-600
              hover:bg-vice-teal-700
              focus:outline-none
              focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
            "
            :disabled="form.processing"
          >
            Save course
          </button>
        </div>
      </div>
    </template>

    <div class="max-w-5xl mx-auto">
      <!-- Form Header -->
      <div class="mt-8 shadow border border-b-0 sm:rounded-t-lg">
        <div class="p-6 bg-light-grey sm:rounded-t-lg">
          <h3 class="text-lg leading-6 font-medium text-vice-teal-700">
            <span class="select-none">Course data</span>
          </h3>
        </div>
      </div>

      <!-- Form Body -->
      <form
        @submit.prevent="submit"
        class="space-y-5 bg-white px-6 py-4 border rounded-b-md shadow mb-6"
      >
        <!-- Organization, Clinicians & Scorm File -->
        <div class="grid grid-cols-2 gap-x-6 gap-y-6 mb-6">
          <!-- University & Clinicians -->
          <div class="space-y-5">
            <div>
              <jet-label for="university_id" value="Select organization*" />
              <select
                @change="universityChanged"
                data-cy="create-course-university"
                id="university_id"
                name="university_id"
                class="
                  mt-1
                  block
                  w-full
                  leading-5
                  border-gray-300
                  focus:ring-vice-teal-500
                  focus:border-vice-teal-500
                  rounded-md
                "
                :class="form.university_id === '' ? 'text-gray-500 text-xs' : 'text-gray-900'"
                v-model="form.university_id"
              >
                <option class="text-gray-500 text-xs" value="" disabled>Select organization</option>
                <option v-for="uni in $page.props.user.universities" :key="uni.id" :value="uni.id">
                  {{ uni.name }}
                </option>
              </select>
              <jet-input-error
                :message="form.errors.university_id || errors.university_id"
                class="mt-2"
              />
            </div>

            <!-- Clinicians -->
            <div>
              <!-- Select clinicians -->
              <jet-label value="Clinicians" class="block text-sm font-medium text-gray-700" />
              <div class="gap-x-2">
                <v-select
                  ref="clinicianSelect"
                  class="
                    mt-1
                    block
                    w-full
                    border-gray-300
                    focus:ring-vice-teal-500
                    focus:border-vice-teal-500
                    rounded-md
                    sm:text-sm
                    text-gray-900
                  "
                  placeholder="Type or select clinician to add"
                  label="name"
                  :options="availableClinicians"
                  @option:selected="onClinicianSelected"
                >
                  <template #open-indicator>
                    <span class="mr-1">
                      <SelectDropdownArrow class="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </span>
                  </template>
                </v-select>
              </div>

              <!-- Selected clinicians table -->
              <div class="my-4 border rounded-lg p-px">
                <table class="table-fixed w-full divide-y divide-gray-200">
                  <thead class="bg-gray-100">
                    <tr>
                      <th
                        scope="col"
                        class="
                          w-2/3
                          whitespace-nowrap
                          px-4
                          py-2
                          text-left text-xxs
                          font-medium
                          text-gray-500
                          tracking-wider
                        "
                      >
                        Clinician
                      </th>
                      <th
                        scope="col"
                        class="
                          w-1/3
                          whitespace-nowrap
                          px-4
                          py-2
                          text-right text-xxs
                          font-medium
                          text-gray-500
                          tracking-wider
                        "
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(cl, index) in selectedClinicians"
                      :key="cl.id"
                      :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                    >
                      <td class="inline-flex space-x-2 px-3 py-2 text-left">
                        <div class="text-gray-400 text-xxs md:text-xs">
                          <ViceAvatar
                            :image="cl.profile_photo_url"
                            :name="cl.name"
                            class="w-8 h-8"
                          />
                        </div>
                        <div class="font-medium content-center text-gray-600 text-xs md:text-sm">
                          {{ cl.name }}
                        </div>
                      </td>
                      <td class="px-2 py-2 whitespace-nowrap text-right">
                        <a href="javascript:void(0)" @click="removeClinician(cl)">
                          <ViceButton small outline red icon="UserRemoveIcon">Remove</ViceButton>
                        </a>
                      </td>
                    </tr>

                    <tr v-if="!selectedClinicians.length">
                      <td colspan="2" class="p-3 whitespace-nowrap text-center">
                        <span class="text-xs text-gray-500">No clinicians selected</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <jet-input-error
                :message="form.errors.clinician_ids || errors.clinician_ids"
                class="mt-2"
              />
            </div>
          </div>

          <!-- Scorm File -->
          <div class="space-y-5">
            <div>
              <jet-label for="scorm_zip" value="SCORM package*" />
              <button
                v-if="this.form.scorm_zip === null"
                type="button"
                class="
                  cursor-pointer
                  relative
                  flex
                  justify-center
                  items-center
                  flex-col
                  w-full
                  border-2 border-gray-300 border-dashed
                  rounded-md
                  py-2
                  px-4
                  mt-1
                  text-center
                  hover:border-gray-400
                  group
                "
              >
                <div class="flex items-center justify-center">
                  <div class="flex space-x-2 leading-5">
                    <div class="flex items-center">
                      <div class="text-left">
                        <span
                          class="text-sm font-medium text-gray-700 group-hover:text-gray-500 block"
                        >
                          <span class="font-bold text-vice-teal-700 underline">
                            Click to upload SCORM package (zip)
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <input
                  :data-cy="'course-scorm-upload-input'"
                  type="file"
                  ref="zipFileInput"
                  multiple
                  accept=".zip"
                  @change="handleFile"
                  class="cursor-pointer opacity-0 absolute h-full w-full"
                />
              </button>

              <!-- Uploaded file -->
              <div v-if="this.form.scorm_zip" class="rounded-md w-full mt-1">
                <div
                  class="
                    p-2
                    min-h-3
                    w-full
                    inline-flex
                    items-center
                    bg-gray-100
                    rounded-md
                    ring-1 ring-vice-teal-600
                    text-sm
                  "
                >
                  <div class="flex items-center">
                    <UploadIcon class="h-8 w-8" aria-hidden="true" pathFill="#0C9488" />
                  </div>
                  <div class="flex-column w-full items-center">
                    <div class="inline-flex w-full">
                      <span class="ml-2 flex-1 w-0 text-gray-800 font-bold truncate">
                        {{ this.form.scorm_zip.name }}
                      </span>
                    </div>
                    <div class="flex-shrink-0 text-gray-500">
                      <span class="ml-2 flex-shrink-0">
                        Size: {{ +(this.form.scorm_zip.size / 1_000_000).toFixed(1) }} MB
                      </span>
                    </div>
                  </div>
                  <div class="flex items-center">
                    <XIcon
                      data-cy="delete-session-files"
                      class="w-6 text-gray-500 cursor-pointer"
                      @click="this.form.scorm_zip = null"
                    />
                  </div>
                </div>
              </div>
              <jet-input-error :message="form.errors.scorm_zip || errors.scorm_zip" class="mt-2" />
            </div>
          </div>
        </div>

        <!-- Title & Slug -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <!-- Title -->
          <div>
            <jet-label for="title" value="Title*" />
            <jet-input
              id="title"
              type="text"
              placeholder="Enter course title"
              class="mt-1 block w-full"
              v-model="form.title"
              @blur="syncSlug"
            />
            <jet-input-error :message="form.errors.title || errors.title" class="mt-2" />
          </div>

          <!-- Slug -->
          <div>
            <jet-label for="slug" value="Link*" />
            <jet-input
              id="slug"
              type="text"
              class="mt-1 block w-full"
              v-model="form.slug"
              placeholder="course-slug"
            />
            <jet-input-error :message="form.errors.slug || errors.slug" class="mt-2" />
          </div>
        </div>

        <!-- Status & Delivery type -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <!-- Status -->
          <div>
            <jet-label for="status" value="Status" />
            <select
              id="status"
              class="mt-1 block w-full rounded-md border-gray-300"
              v-model="form.status"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
            <jet-input-error :message="form.errors.status || errors.status" class="mt-2" />
          </div>

          <!-- Delivery type -->
          <div>
            <jet-label for="delivery_type" value="Delivery type" />
            <select
              id="delivery_type"
              class="mt-1 block w-full rounded-md border-gray-300"
              v-model="form.delivery_type"
            >
              <option value="on_demand">On demand</option>
              <option value="live">Live</option>
            </select>
            <jet-input-error
              :message="form.errors.delivery_type || errors.delivery_type"
              class="mt-2"
            />
          </div>
        </div>

        <!-- Delivery Type Dates -->
        <div v-if="form.delivery_type === 'live'" class="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <!-- Start date -->
          <div>
            <jet-label for="live_start_at" value="Start date*" />
            <date-picker
              ref="datepicker"
              v-model="form.live_start_at"
              :min-date="new Date()"
              :popover="{ visibility: 'click' }"
              :model-config="datepickerConfig"
              :masks="{ weekdays: 'WWW', input: 'DD MMMM YYYY' }"
            >
              <template v-slot="{ inputValue, inputEvents }">
                <div class="input-with-icon">
                  <jet-input
                    data-cy="live-start-at"
                    ref="date"
                    name="live_start_at"
                    id="live_start_at"
                    class="
                      mt-1
                      focus:outline-none
                      focus:ring-vice-teal-500
                      focus:border-vice-teal-500
                      focus:ring-1
                      w-full
                      p-2
                      border border-gray-300
                      text-sm
                      rounded-md
                    "
                    placeholder="Select start date"
                    v-on="inputEvents"
                    :value="inputValue"
                  />
                  <CalendarIcon class="calendar-icon" />
                </div>
              </template>
            </date-picker>
            <jet-input-error
              :message="form.errors.live_start_at || errors.live_start_at"
              class="mt-2"
            />
          </div>

          <!-- End date -->
          <div>
            <jet-label for="live_end_at" value="End date*" />
            <date-picker
              ref="datepicker"
              v-model="form.live_end_at"
              :min-date="new Date()"
              :popover="{ visibility: 'click' }"
              :model-config="datepickerConfig"
              :masks="{ weekdays: 'WWW', input: 'DD MMMM YYYY' }"
            >
              <template v-slot="{ inputValue, inputEvents }">
                <div class="input-with-icon">
                  <jet-input
                    data-cy="live-end-at"
                    ref="date"
                    name="live_end_at"
                    id="live_end_at"
                    class="
                      mt-1
                      focus:outline-none
                      focus:ring-vice-teal-500
                      focus:border-vice-teal-500
                      focus:ring-1
                      w-full
                      p-2
                      border border-gray-300
                      text-sm
                      rounded-md
                    "
                    placeholder="Select end date"
                    v-on="inputEvents"
                    :value="inputValue"
                  />
                  <CalendarIcon class="calendar-icon" />
                </div>
              </template>
            </date-picker>
            <jet-input-error
              :message="form.errors.live_end_at || errors.live_end_at"
              class="mt-2"
            />
          </div>
        </div>

        <!-- About this course -->
        <div>
          <jet-label for="summary" value="About this course" />
          <textarea
            id="summary"
            placeholder="Provide a short description of what students can expect from this course..."
            class="
              mt-1
              block
              w-full
              rounded-md
              border-gray-300
              focus:ring-vice-teal-500
              focus:border-vice-teal-500
            "
            rows="4"
            v-model="form.summary"
          ></textarea>
          <jet-input-error :message="form.errors.summary || errors.summary" class="mt-2" />
        </div>

        <!-- Background -->
        <div>
          <jet-label for="background" value="Background" />
          <textarea
            id="background"
            placeholder="Explain the background context, why this course exists and who it is designed for..."
            class="
              mt-1
              block
              w-full
              rounded-md
              border-gray-300
              focus:ring-vice-teal-500
              focus:border-vice-teal-500
            "
            rows="4"
            v-model="form.background"
          ></textarea>
          <jet-input-error :message="form.errors.background || errors.background" class="mt-2" />
        </div>

        <!-- What you will learn -->
        <div>
          <jet-label for="learning_outcomes" value="What you will learn" />
          <div
            v-for="(outcome, index) in form.learning_outcomes"
            :key="index"
            class="flex items-center space-x-2 mt-2"
          >
            <jet-input
              type="text"
              class="block w-full"
              v-model="form.learning_outcomes[index]"
              placeholder="e.g. Master the basics of clinical practice"
            />
            <button
              type="button"
              @click="removeOutcome(index)"
              class="p-2 text-gray-400 hover:text-red-500"
            >
              <XIcon class="w-5 h-5" />
            </button>
          </div>
          <button
            type="button"
            @click="addOutcome"
            class="
              mt-2
              inline-flex
              items-center
              text-sm text-vice-teal-600
              hover:text-vice-teal-700
            "
          >
            <span class="mr-1 text-lg">+</span>
            Add learning outcome
          </button>
          <jet-input-error
            :message="form.errors.learning_outcomes || errors.learning_outcomes"
            class="mt-2"
          />
        </div>

        <!-- Enrollment open -->
        <!-- <div class="flex items-center">-->
        <!--   <input-->
        <!--     id="enrolment_open"-->
        <!--     type="checkbox"-->
        <!--     class="rounded border-gray-300"-->
        <!--     v-model="form.enrolment_open"-->
        <!--   />-->
        <!--   <label for="enrolment_open" class="ml-2 text-sm text-gray-700">Enrolment open</label>-->
        <!-- </div>-->

        <div class="grid grid-cols-2 gap-x-6 gap-y-6 mb-6">
          <UserInvitationList
            user-type="student"
            :available-users="availableStudents"
            @update:selected="onStudentsChanged"
          />
          <jet-input-error :message="form.errors.student_ids" class="mt-2" />
          <jet-input-error :message="form.errors.student_emails" class="mt-2" />
        </div>
      </form>
    </div>
  </clinician-app-layout>
</template>

<script>
import vSelect from 'vue-select';
import { ChevronLeftIcon, XIcon } from '@heroicons/vue/solid';
import { CalendarIcon } from '@heroicons/vue/outline';
import { DatePicker } from 'v-calendar';
import ClinicianAppLayout from '@/Layouts/ClinicianAppLayout.vue';
import JetInput from '@/Jetstream/Input.vue';
import JetLabel from '@/Jetstream/Label.vue';
import UserInvitationList from '@/Components/UserInvitationList.vue';
import JetInputError from '@/Jetstream/InputError.vue';
import SelectDropdownArrow from '@/Components/Icons/SelectDropdownArrow.vue';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import ViceAvatar from '@/Components/Avatars/ViceAvatar.vue';
import UploadIcon from '@/Components/Icons/UploadIcon.vue';
import 'v-calendar/dist/style.css';
import '../../../../css/date-picker.css';

export default {
  components: {
    DatePicker,
    CalendarIcon,
    XIcon,
    UploadIcon,
    ViceAvatar,
    ChevronLeftIcon,
    ViceButton,
    SelectDropdownArrow,
    ClinicianAppLayout,
    JetInput,
    JetLabel,
    JetInputError,
    vSelect,
    UserInvitationList,
  },
  props: {
    clinicians: Array,
    students: Array,
    sessions: Array,
    errors: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      progress: null,
      highlightedInGroup: null,
      selectedClinicians: [],
      selectedStudents: [],
      datepickerConfig: {
        type: 'string',
        mask: 'YYYY-MM-DD',
      },
      form: this.$inertia.form({
        title: '',
        summary: '',
        background: '',
        learning_outcomes: [],
        slug: '',
        status: 'draft',
        enrolment_open: false,
        delivery_type: 'on_demand',
        live_start_at: null,
        live_end_at: null,
        university_id: '',
        clinician_ids: [],
        student_ids: [],
        student_emails: [],
        scorm_zip: null,
      }),
    };
  },
  computed: {
    availableClinicians() {
      let allAvailable = this.clinicians;
      if (this.form.university_id) {
        allAvailable = allAvailable.filter((cl) =>
          cl.universities.some((uni) => uni.id === this.form.university_id),
        );
      }
      return allAvailable.filter(
        (c) => !this.selectedClinicians.some((selected) => selected.id === c.id),
      );
    },
    availableStudents() {
      let allAvailable = this.students;
      if (this.form.university_id) {
        allAvailable = allAvailable.filter((s) => s.university_id === this.form.university_id);
      }

      const notSelected = allAvailable.filter(
        (s) => !this.selectedStudents.some((selected) => selected.id === s.id),
      );

      // Group students by group name
      const groupsMap = {};
      const studentsWithoutGroups = [];

      notSelected.forEach((student) => {
        if (student.groups && student.groups.length > 0) {
          student.groups.forEach((group) => {
            if (!groupsMap[group.name]) {
              groupsMap[group.name] = {
                id: `group-${group.id}`,
                name: group.name,
                isGroup: true,
                students: [],
              };
            }
            groupsMap[group.name].students.push(student);
          });
        } else {
          studentsWithoutGroups.push(student);
        }
      });

      const options = [];

      // Add groups that have students
      Object.values(groupsMap).forEach((group) => {
        if (group.students.length > 0) {
          options.push(group);
          group.students.forEach((s) => {
            options.push({
              ...s,
              groupName: group.name,
            });
          });
        }
      });

      // Add students without groups
      studentsWithoutGroups.forEach((s) => {
        options.push(s);
      });

      return options;
    },
  },
  watch: {
    selectedClinicians: {
      deep: true,
      handler() {
        this.form.clinician_ids = this.selectedClinicians.map((c) => c.id);
      },
    },
  },
  methods: {
    onClinicianSelected(clinician) {
      this.selectedClinicians.push(clinician);
      this.$refs.clinicianSelect.clearSelection();
    },

    onStudentsChanged(selected) {
      this.form.student_ids = selected.filter((s) => s.id).map((s) => s.id);
      this.form.student_emails = selected.filter((s) => !s.id).map((s) => s.email);
    },

    addOutcome() {
      this.form.learning_outcomes.push('');
    },

    removeOutcome(index) {
      this.form.learning_outcomes.splice(index, 1);
    },

    removeClinician(clinician) {
      this.selectedClinicians = this.selectedClinicians.filter((c) => c.id !== clinician.id);
    },
    universityChanged() {
      this.selectedClinicians = this.selectedClinicians.filter((cl) =>
        cl.universities.some((uni) => uni.id === this.form.university_id),
      );
    },
    syncSlug() {
      this.form.slug = (this.form.title || '')
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .substring(0, 180);
    },
    handleFile(e) {
      const [file] = e.target.files;
      this.form.scorm_zip = file || null;
    },
    submit() {
      this.progress = null;
      this.form.post(route('admin.learning-courses.store'), {
        forceFormData: true,
        onProgress: (event) => {
          this.progress = Math.round((event.loaded / event.total) * 100);
        },
      });
    },
  },
};
</script>
<style lang="scss" scoped>
::placeholder {
  color: rgb(136, 139, 136) !important;
  font-size: 14px;
}

.vs__search,
.vs__dropdown-toggle,
.vs__selected-options {
  background: none;
  //border-color: #d1d5db;
  margin-top: 0;
}

.vs__search {
  width: 100%;
  border-radius: 0.375rem;
  --tw-border-opacity: 1;
}
//
//.vs__actions {
//  cursor: pointer;
//  display: flex;
//  position: relative;
//  bottom: 2rem;
//  justify-content: flex-end;
//  width: 100%;
//}

.vs__actions span svg {
  height: 1.5rem;
  width: 1.5rem;
  margin-right: 0.25rem;
}

.vs__dropdown-menu {
  background: #ffffff;
  color: var(--ViCE---Dark-Grey, #606367);
  font-size: 14px;

  & .vs__dropdown-option--highlight {
    background-color: rgba(45, 212, 191, var(--tw-bg-opacity));
  }

  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 0 6px 0;
  margin-top: 2px;
  position: relative;
  z-index: 9999;
  opacity: 95%;
}

.vs__dropdown-menu .vs__dropdown-option {
  padding: 0.25rem 1rem;
  //border-radius: 6px;
  margin: 0 6px;
}

.vs--searchable .vs__dropdown-toggle {
  width: 100%;
  border-radius: 0.375rem;
  --tw-border-opacity: 1;
  //height: 41px;
}

.vs--open .vs__dropdown-toggle {
  border-bottom-color: #d1d5db;
  font-size: 14px;
  color: rgb(136, 139, 136) !important;
}

.vs__no-options {
  padding: 0 12px;
}

.grey-icon {
  color: #9ca3af;
}

.input-with-icon {
  position: relative;
}

.input-with-icon input {
  padding-left: 35px; /* Make room for the icon */
}

.calendar-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  color: #656262;
}
</style>
